import numeral from 'numeral';
import {
  defaultTo,
  size,
  sortBy,
  head,
  last,
  filter,
  isNil,
  startCase,
} from 'lodash';

export default {
  bets: (state) => state.config.bets,
  coldNumbers: (state) => state.statistics?.drawnLeastTimes,
  currentDrawBall: (state) => state.gameState?.ball,
  isCursorEnabled: (state) => state.config?.settingsData?.showCursor ?? true, // if we don't have show cursor we are on the web and we need to enable it
  deviceType: (state) => (state.isMobile ? 'mobile' : 'desktop'),
  getTranslation: (state, getters) => (key) =>
    defaultTo(getters.translations[key], key),
  getFormattedNumber: (getters) => (number) =>
    numeral(number).format(getters.numbersFormat),
  hotNumbers: (state) => state.statistics?.drawnMostTimes,
  isNextRoundRawEvents(state) {
    return (
      state.gameState?.type === 'ball' ||
      state.gameState?.type === 'results' ||
      state.gameState?.type === 'new'
    );
  },
  isNextRound(state, getters) {
    return (
      getters.isStartEvent ||
      getters.isDrawResultEvent ||
      getters.isSetResultsEvent ||
      getters.isNextRoundRawEvents
    );
  },
  isPayinDisabled: (state, getters) =>
    state.isPayinInProcess || getters.isBettingDisabled,
  isBettingDisabled: (state) => state.gameState?.eventName === 'StopBetting',
  isStartEvent: (state) => state.gameState?.eventName === 'StartEvent',
  isDrawResultEvent: (state) => state.gameState?.eventName === 'DrawResult',
  isSetResultsEvent: (state) => state.gameState?.eventName === 'SetResults',
  isStartCountdownEvent: (state) =>
    state.gameState?.eventName === 'StartCountdown',
  isTerminalLayout: (state) => state.layout === 'terminal',
  isTerminalChannel: (state) =>
    state.config.applicationName?.toLowerCase() === 'terminal',
  isNextGen: (state) => state.config.platformName === 'NextGen',
  isSevenPlatform: (state) => state.config.platformName === 'seven',
  layoutClass: (state, getters) => {
    if (getters.isMobile) return 'mobile-layout';

    return getters.isDesktop ? 'expanded-layout' : 'compact-layout';
  },
  maxSystemNumber: (state, getters) => getters.rules.maxSystemNumber.value,
  maxBetNumber: (state, getters) => getters.rules.maxBetNumber.value,
  maxNoDrawNumber: (state, getters) => getters.rules.maxNoDrawNumbers.value,
  maxAllInNumber: (state, getters) => getters.rules.maxAllInNumbers.value,
  minCombBetAmount: (state, getters) => getters.rules.minCombBetAmount.value,
  minBetAmount: (state, getters) => getters.rules.minBetAmount.value,
  minSystemNumber: (state) => {
    const { suggestedSystems } = state;

    if (size(suggestedSystems) === 0) {
      return null;
    }

    const [minSystemNumber] = Object.keys(suggestedSystems)[0];

    return Number(minSystemNumber);
  },
  spotNumberRange: (state) => {
    const { paytable } = state;

    const spotFilter = (payItem) => {
      const [firstNumber, secondNumber] = payItem.split('_').map(Number);

      return firstNumber === secondNumber;
    };

    const spots = Object.keys(paytable).filter(spotFilter);
    const minSpot = Number(head(spots).split('_')[0]);
    const maxSpot = Number(last(spots).split('_')[0]);

    return {
      minSpot,
      maxSpot,
    };
  },
  systemConfig: (state, getters) => getters.bets.systemOnOff,
  roundId: (state) => state.gameState?.eventId,
  quickpickNumbersItem: (state, getters) => {
    const { spotNumberRange } = getters;

    if (isNil(state.quickpickNumbersItem)) {
      return spotNumberRange.minSpot;
    }

    return state.quickpickNumbersItem;
  },
  specials: (state, getters) => {
    const BET_IDS_TO_IGNORE = [40, 41];
    const { bets } = getters;
    const filteredBets = filter(bets, (bet) => bet.betType === 'Special');
    const filteredSpecials = filteredBets.filter(
      (bet) => !BET_IDS_TO_IGNORE.includes(bet.id),
    );

    return filteredSpecials;
  },
  specialsTurnedOff: (state, getters) =>
    getters.specials.every((special) => !special.value),
  selectedNumbersForPayin: (state) => {
    const { selectedNumbers } = state;

    return sortBy(selectedNumbers.map((number) => Number(number.value)));
  },
  selectedNumbersLength: (state) => state.selectedNumbers?.length,
  savedSettings: () => {},
  ui: (state) => state.config.ui,
  theme: (state, getters) => getters.ui.config.theme,
  user: (state, getters, rootState, rootGetters) => rootGetters['user/user'],
  userBalance: (state, getters) => getters.user.balance,
  ajsDisabled: (state) => state.config?.ui?.config?.ajsDisabled ?? false,
  isVisualizationV2Active: (state) =>
    state.config?.ui?.config?.kenoV2Visualization !== false,
  isBoostActive: (state) => state.config.ui.config?.boost?.enabled || false,
  userIsOperator: (state) =>
    state.terminalUserData.userType === 'OPERATOR' ||
    state.terminalUserData.type === 'OPERATOR',
  freeBetFeatureActive: (state) => state.config.ui.config.freeBetFeatureActive,
  customDomain: (state) => state.config.customDomain,
  customProductName: (state) => state.config.ui?.config?.customProductName,
  // TODO: Remove true default when all apps adapted and ensured all clients with custom/translated product name are adapted through static config
  translateProductName: (state) =>
    state.config.ui?.config?.translateProductName ?? true,
  productNameLabel: (state) =>
    state.usePackageTranslations ? 'product_name' : 'keno_title',
  gameTitle: (state, getters) => {
    if (!isNil(getters.customProductName)) {
      return getters.customProductName;
    }
    return getters.translateProductName
      ? getters.translations?.[getters.productNameLabel]
      : startCase(state.config?.productName) || '';
  },
};
